import { useApMismatchAlertSettings } from '@/context/services/ap_mismatch_alert_settings/ApMismatchAlertSettings.service';
import {
  toEnabledTargetKeys,
  TOTAL_AMOUNT_TARGET_KEY,
} from '@/context/services/ap_mismatch_alert_settings/utils';
import { useGetReportInferences } from '@/context/services/report_inferences/ReportInferences.service';
import {
  reportFormInputType,
  type InvoiceReportFormInputResponse,
} from '@/context/services/report_inferences/types';
import {
  getMismatch,
  getPayeeBankMismatch,
  toComparableDueDate,
  toComparableTotalAmount,
  toMismatchMessage,
  toNormalizedTotalAmountInference,
} from '@/context/services/report_inferences/utils';
import { useDataExtractionMismatchAlertFlag } from '@/hooks/feature_flag/useDataExtractionMismatchAlertFlag';
import { useDataExtractionMismatchAlertPhase2Flag } from '@/hooks/feature_flag/useDataExtractionMismatchAlertPhase2Flag';
import { useMismatchAlert } from '@/hooks/invoice_reports/useMismatchAlert';
import { useTranslation } from '@/i18n';
import { useMemo } from 'react';

export type ReportInferencesMismatchViewData = {
  fieldCaptionsByInputId: Partial<Record<string, string>>;
  paymentTotalCaption?: string;
};

export type ReportInferencesCurrentDataValues = {
  reportFormInputs?: InvoiceReportFormInputResponse[] | null;
  paymentTotalAmount?: string | null;
};

const isPayeeInputType = (type: string | undefined | null): boolean =>
  type === reportFormInputType.ApReportFormInputInvoicePayeeSelect ||
  type === reportFormInputType.ApReportFormInputInvoiceInstantPayee;

const hasAccountNumber = (
  value: unknown
): value is { account_number?: string | null } =>
  typeof value === 'object' && value !== null && 'account_number' in value;

const isNonEmptyAccountNumber = (
  value: unknown
): value is { account_number: string } =>
  hasAccountNumber(value) &&
  typeof value.account_number === 'string' &&
  value.account_number.trim() !== '';

const hasField = (
  value: unknown,
  key: string
): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && key in value;

const getFlatStringField = (
  value: unknown,
  key: 'bank_name' | 'bank_branch_name'
): string | undefined => {
  if (!hasField(value, key)) return undefined;

  const fieldValue = value[key];
  if (typeof fieldValue === 'string') {
    const trimmed = fieldValue.trim();
    return trimmed === '' ? undefined : trimmed;
  }
  return undefined;
};

type SelectedPayeeBankDetails = {
  accountNumber: string | undefined;
  bankName: string | undefined;
  bankBranch: string | undefined;
};

const getSelectedPayeeBankDetails = (
  inputs: InvoiceReportFormInputResponse[] | null | undefined
): SelectedPayeeBankDetails => {
  const payeeInputs =
    inputs?.filter(({ type }) => isPayeeInputType(type)) ?? [];

  let accountNumber: string | undefined;
  let bankName: string | undefined;
  let bankBranch: string | undefined;

  for (const item of payeeInputs) {
    for (const additionalValue of item.input_additional_values ?? []) {
      if (!accountNumber && isNonEmptyAccountNumber(additionalValue)) {
        accountNumber = additionalValue.account_number;
      }
      if (bankName === undefined) {
        bankName = getFlatStringField(additionalValue, 'bank_name');
      }
      if (bankBranch === undefined) {
        bankBranch = getFlatStringField(additionalValue, 'bank_branch_name');
      }
    }
  }

  return {
    accountNumber,
    bankName,
    bankBranch,
  };
};

const hasInstantPayeeInput = (
  inputs: InvoiceReportFormInputResponse[] | null | undefined
): boolean =>
  inputs?.some(
    ({ type }) =>
      type === reportFormInputType.ApReportFormInputInvoiceInstantPayee
  ) ?? false;

const hasNonEmptyAccountNumberInInput = (
  item: InvoiceReportFormInputResponse
): boolean =>
  item.input_additional_values?.some(isNonEmptyAccountNumber) ?? false;

const hasAccountNumberFieldInInput = (
  item: InvoiceReportFormInputResponse
): boolean => item.input_additional_values?.some(hasAccountNumber) ?? false;

const getPayeeCaptionTargetIndex = (
  inputs: InvoiceReportFormInputResponse[] | null | undefined
): number => {
  const list = inputs ?? [];
  const accountNumberRowIndex = list.findIndex(
    (item) =>
      isPayeeInputType(item.type) && hasNonEmptyAccountNumberInInput(item)
  );
  if (accountNumberRowIndex >= 0) return accountNumberRowIndex;

  // No row has a non-empty account_number (e.g. the payee has none
  // registered), but a mismatch should still be shown on the payment method
  // row specifically. Identify it structurally by the account_number field
  // being present at all (even null/empty), rather than by position.
  return list.findIndex(
    (item) => isPayeeInputType(item.type) && hasAccountNumberFieldInInput(item)
  );
};

// `itemIndex` is required: payee rows share the same form input id, so the row
// can only be identified by its position. Callers render with
// `reportFormInputs.map((item, index) => ...)` and must pass that index.
export const getShownMismatchCaptionForDefaultField = (
  item: InvoiceReportFormInputResponse,
  mismatchCaption: string | undefined,
  reportFormInputs: InvoiceReportFormInputResponse[] | null | undefined,
  itemIndex: number
): string | undefined => {
  if (!mismatchCaption) {
    return undefined;
  }

  // Non-payee rows keep the default behavior: show the caption on the row itself.
  if (!isPayeeInputType(item.type)) {
    return mismatchCaption;
  }

  // Payee is expanded into multiple rows (e.g. name/code/payment method) that
  // all share the same form input id, so the row cannot be told apart by id.
  // See `getPayeeCaptionTargetIndex` for how the target row is chosen when no
  // row has a non-empty account_number (e.g. the payee has none registered).
  const payeeCaptionTargetIndex = getPayeeCaptionTargetIndex(reportFormInputs);
  if (payeeCaptionTargetIndex < 0) {
    return undefined;
  }

  return itemIndex === payeeCaptionTargetIndex ? mismatchCaption : undefined;
};

export const useReportInferencesMismatchCaptions = (
  reportId: string,
  reportTypeId: string,
  currentData?: ReportInferencesCurrentDataValues
): ReportInferencesMismatchViewData | undefined => {
  const { t } = useTranslation();
  const isMismatchAlertFlag = useDataExtractionMismatchAlertFlag();
  const isMismatchAlertPhase2Flag = useDataExtractionMismatchAlertPhase2Flag();
  const settings = useApMismatchAlertSettings(isMismatchAlertFlag);

  // `toEnabledTargetKeys` returns an empty set when the report type itself is
  // disabled, so a non-empty set means at least one field wants the alert.
  // The inference fetch is gated on this to avoid requesting data nobody uses.
  const isEnabled =
    toEnabledTargetKeys(settings.data ?? [], reportTypeId).size > 0;

  const inferenceResponse = useGetReportInferences(reportId, isEnabled);

  const inferences = useMemo(() => {
    const responseInferences = inferenceResponse?.inferences;
    if (responseInferences && responseInferences.length > 0) {
      return responseInferences;
    }
    return [];
  }, [inferenceResponse?.inferences]);

  const reportFormInputs = currentData?.reportFormInputs;
  const paymentTotalAmount = currentData?.paymentTotalAmount;

  const isSpotPayee = hasInstantPayeeInput(reportFormInputs);

  const { inferencesMap, isFieldAlertEnabled } = useMismatchAlert(
    inferences,
    reportTypeId,
    { isSpotPayee }
  );

  const mismatchViewData = useMemo(() => {
    if (!isEnabled || !inferenceResponse) {
      return undefined;
    }

    const enabledReportFormInputs =
      reportFormInputs?.filter((item) => isFieldAlertEnabled(item.id)) ?? [];
    const selectedPayeeBankDetails = getSelectedPayeeBankDetails(
      enabledReportFormInputs
    );
    const payeeTargetInput =
      enabledReportFormInputs[
        getPayeeCaptionTargetIndex(enabledReportFormInputs)
      ];

    const payeeMismatch = getPayeeBankMismatch(
      {
        bankName: inferencesMap.payeeBankName,
        bankBranch: inferencesMap.payeeBankBranch,
        accountNumber: inferencesMap.apPayee,
      },
      selectedPayeeBankDetails
    );
    // Spot payee (スポット支払先) comparison is Phase 2 only.
    const payeeCaption =
      isSpotPayee && !isMismatchAlertPhase2Flag
        ? undefined
        : toMismatchMessage(payeeMismatch, t, 'text');
    const fieldCaptionsByInputId: Partial<Record<string, string>> = {};
    if (payeeTargetInput?.id && payeeCaption) {
      fieldCaptionsByInputId[payeeTargetInput.id] = payeeCaption;
    }

    for (const input of enabledReportFormInputs) {
      const currentValue = input.input_values?.[0]?.value;

      if (
        input.type === reportFormInputType.ApReportFormInputInvoiceDueDateField
      ) {
        const dueDateMismatch = getMismatch(
          inferencesMap.dueDate,
          toComparableDueDate(currentValue)
        );
        const caption = toMismatchMessage(dueDateMismatch, t, 'date');
        if (caption) fieldCaptionsByInputId[input.id] = caption;
      }

      if (
        input.type ===
        reportFormInputType.ApReportFormInputInvoiceRegistrationNumberField
      ) {
        const registrationNumberMismatch = getMismatch(
          inferencesMap.invoiceRegistrationNumber,
          currentValue
        );
        const caption = toMismatchMessage(
          registrationNumberMismatch,
          t,
          'text'
        );
        if (caption) fieldCaptionsByInputId[input.id] = caption;
      }
    }

    const totalAmountMismatch = isFieldAlertEnabled(TOTAL_AMOUNT_TARGET_KEY)
      ? getMismatch(
          toNormalizedTotalAmountInference(inferencesMap.totalAmount),
          toComparableTotalAmount(paymentTotalAmount)
        )
      : null;

    return {
      fieldCaptionsByInputId,
      paymentTotalCaption: toMismatchMessage(totalAmountMismatch, t, 'amount'),
    };
  }, [
    inferenceResponse,
    inferencesMap,
    isFieldAlertEnabled,
    isEnabled,
    isMismatchAlertPhase2Flag,
    isSpotPayee,
    paymentTotalAmount,
    reportFormInputs,
    t,
  ]);

  return mismatchViewData;
};
